import * as _ from 'lodash';
import { IBaseMutableModel } from 'src/app/interfaces/base-mutable-model.interface';

import { FieldType } from '../../../shared/enums';
import { CalibrationSetModel } from '../calibrations/calibration-set.model';
import { IFieldSettings } from 'src/app/interfaces/field-settings.interface';
import { IBaseFieldComponent } from 'src/app/interfaces/base-field-component.interface';
import { IFieldValidationResult } from 'src/app/interfaces/zc-validation-result.interface';

export abstract class BaseMutableModel<T> implements IBaseMutableModel<T> {
    private properties: string[] = [];
    private keyProperties = [];
    private propertiesToDeclare = [];

    origin: T = {} as any;
    currentState: T = {} as any;
    fieldsSettings: { [property: string]: IFieldSettings } = {};
    private _fieldsErrors = {};
    private _isOriginalState: { [property: string]: boolean } = {};
    /**
     * This registry is used to keep links to components that used to edit properties values.
     * Because we decided to limit number of change detection operation each time any property changed,
     * we sometimes should trigger change detection manually.
     * This registry is used to access to the components if needed.
     */
    private _propertyEditorComponents: { [property: string]: IBaseFieldComponent } = {};

    constructor(
        data: any,
        keyProperties?: string[],
        fieldSettings?: { [property: string]: IFieldSettings },
        propertiesToDeclare: string[] = []
    ) {
        this.updateValues(data);

        this.properties = _.keys(this.currentState);
        this.keyProperties = keyProperties;
        this.propertiesToDeclare = propertiesToDeclare;

        if (fieldSettings) {
            this.fieldsSettings = fieldSettings;
        }

        this.propertiesToDeclare.forEach(propertyName => {
            Object.defineProperty(this, propertyName, {
                get() {
                    return this.currentState[propertyName];
                },
                set(value) {
                    this.currentState[propertyName] = value;
                }
            });
        });

        // This inits initial model's fields state
        this.properties.forEach(property => {
            this._isOriginalState[property] = true;
        });
    }

    get isChanged(): boolean {
        return !_.isEqual(this.origin, this.currentState);
    }

    // TODO :: refactor. BaseMutableModel should know nothing about CalibrationSetModel
    isValid(calibrationSet?: CalibrationSetModel<T>, ignoreRequiredFields = false): boolean {
        return !this.properties.some(property => {
            const errors = this.getErrorMessagesForField(property, calibrationSet, ignoreRequiredFields);
            return !!errors.length;
        });
    }

    updateValues(updatedValue) {
        Object.assign(this.origin, updatedValue);
        Object.assign(this.currentState, updatedValue);
    }

    setDefault(keyData) {
        if (this.currentState['Id']) {
            delete this.currentState['Id'];
            delete this.origin['Id'];
        }
        if (keyData) {
            Object.assign(this.origin, keyData);
        }
        Object.assign(this.currentState, this.origin);
    }

    getChangedValues() {
        if (!this.isChanged) {
            return;
        }

        const changedProperties = this.properties.filter(property => {
            return this.origin[property] !== this.currentState[property];
        });

        return _.pick(this.currentState, changedProperties.concat(this.keyProperties));
    }

    isFieldChanged(property: string) {
        const field = this.fieldsSettings[property];
        // eslint-disable-next-line eqeqeq
        if (field && field.type == FieldType.BOOLEAN) {
            // eslint-disable-next-line eqeqeq
            return !!this.origin[property] != !!this.currentState[property];
        }
        return this.origin[property] !== this.currentState[property];
    }

    // TODO - Rename to `cancelChanges` for uniformity w/ Overlays, ZoneCombinations, and others
    removeChanges() {
        Object.assign(this.currentState, this.origin);
    }

    getErrorMessagesForField(
        property: string,
        calibrationSet?: CalibrationSetModel<T>,
        ignoreRequiredFields = false
    ): string[] {
        const field = this.fieldsSettings[property];

        if (!field) {
            return [];
        }

        this._fieldsErrors[property] = field.validate({
            model: this,
            currentModelState: this.currentState,
            calibrationSet,
            ignoreRequiredFields
        });

        return this._fieldsErrors[property];
    }

    clone() {
        return new (this.constructor as any)(this.currentState, this.keyProperties, this.fieldsSettings);
    }

    acceptChanges() {
        Object.assign(this.origin, this.currentState);
    }

    setCurrentStateValue(fieldName: string, value: any) {
        if (!fieldName) {
            return;
        }
        this.currentState[fieldName] = value;
    }

    getValidationResults(
        calibrationSet?: CalibrationSetModel<T>,
        ignoreRequiredFields = false
    ): { [property: string]: IFieldValidationResult } {
        const result: { [property: string]: IFieldValidationResult } = {};
        this.properties.forEach(property => {
            result[property] = {
                isValid: this.getErrorMessagesForField(property, calibrationSet, ignoreRequiredFields),
                ignoreRequiredFields,
                isOriginalState: this.getIsOriginalState(property),
                label: _.get(this, `fieldsSettings["${property}"].label`, '')
            };
        });
        return result;
    }

    markManuallyChangedProperty(propertyName: string) {
        if (!propertyName) {
            return;
        }
        this._isOriginalState[propertyName] = false;
    }

    getIsOriginalState(propertyName: string) {
        if (!propertyName) {
            return;
        }
        return this._isOriginalState[propertyName];
    }

    registerPropertyEditorComponent(propertyName: string, component: IBaseFieldComponent) {
        if (!propertyName || !component) {
            return;
        }

        this._propertyEditorComponents[propertyName] = component;
    }

    getPropertyEditorComponent(propertyName: string): IBaseFieldComponent {
        return propertyName ? this._propertyEditorComponents[propertyName] : null;
    }

    triggerPropertyEditorChangeDetection(propertyName: string) {
        const component = this.getPropertyEditorComponent(propertyName);
        if (!component) {
            return;
        }
        component.triggerChangeDetection();
    }
}
